import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Organization } from '../types/admin';

interface OrgsResponse {
  success: boolean;
  data: Organization[];
}

interface OrgResponse {
  success: boolean;
  data: Organization;
}

export function useOrganizations() {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<OrgsResponse>('/admin/organizations');
      setOrgs(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  const createOrg = useCallback(
    async (data: Omit<Organization, 'id' | 'createdAt' | 'updatedAt'>) => {
      const res = await api.post<OrgResponse>('/admin/organizations', data);
      setOrgs(prev => [...prev, res.data]);
      return res.data;
    },
    [],
  );

  return { orgs, loading, error, createOrg, refetch: fetchOrgs };
}

export function useOrg(orgId: string) {
  const [org, setOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrg = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<OrgResponse>(`/admin/organizations/${orgId}`);
      setOrg(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organization');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchOrg();
  }, [fetchOrg]);

  const updateOrg = useCallback(
    async (data: Partial<Omit<Organization, 'id' | 'createdAt' | 'updatedAt'>>) => {
      const res = await api.patch<OrgResponse>(`/admin/organizations/${orgId}`, data);
      setOrg(res.data);
      return res.data;
    },
    [orgId],
  );

  return { org, loading, error, updateOrg, refetch: fetchOrg };
}
